import React from 'react';
import Typography, { TypographyProps } from '@mui/material/Typography';

/**
 * Shared typography primitives for the PipeEX React frontend.
 *
 * These thin wrappers around MUI Typography enforce the canonical type scale
 * defined in src/theme/theme.ts. They should be used in place of ad-hoc
 * fontSize/fontWeight/lineHeight overrides so every page stays consistent.
 */

/** Props shared by all primitives. `variant` is fixed by each component. */
type BaseTypographyProps = Omit<TypographyProps, 'variant'>;

export const PageTitle: React.FC<BaseTypographyProps> = (props) => (
  <Typography variant="h4" {...props} />
);

export const CompactPageTitle: React.FC<BaseTypographyProps> = (props) => (
  <Typography variant="h5" {...props} />
);

export const SectionTitle: React.FC<BaseTypographyProps> = (props) => (
  <Typography variant="h6" {...props} />
);

export const SubsectionTitle: React.FC<BaseTypographyProps> = (props) => (
  <Typography variant="subtitle1" {...props} />
);

export const CardTitle: React.FC<BaseTypographyProps> = (props) => (
  <Typography variant="subtitle2" {...props} />
);

export type BodyTextProps = BaseTypographyProps & {
  /** Use body2 (smaller, secondary color) instead of body1. */
  secondary?: boolean;
};

export const BodyText: React.FC<BodyTextProps> = ({ secondary, ...props }) => (
  <Typography variant={secondary ? 'body2' : 'body1'} {...props} />
);

export const CaptionText: React.FC<BaseTypographyProps> = (props) => (
  <Typography variant="caption" {...props} />
);

export type OverlineTextProps = BaseTypographyProps & {
  /** Keep the uppercase transform. Pass false for sentence-case overlines. */
  uppercase?: boolean;
};

export const OverlineText: React.FC<OverlineTextProps> = ({
  uppercase = true,
  sx,
  ...props
}) => (
  <Typography
    variant="overline"
    sx={[
      uppercase ? { textTransform: 'uppercase' } : { textTransform: 'none' },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
);
